import React, { useContext, useEffect, useState } from "react";
import {
  initiateCreditCheck,
  submitExistingLoans,
} from "../../../swr/mortgage";
import {
  InitiateCreditCheckRequest,
  InitiateCreditCheckResData,
  SubmitExistingLoansRequest,
} from "../../../types/mortgage";
import LoadingContext from "../../../contexts/LoadingContext";
import ToastContext from "../../../contexts/ToastContext";
import { useHistory } from "react-router";

type UseActiveLoansProp = {
  mortgageApplicationId: number | null;
  location: number | undefined;
  encodedBvn: string | null;
  encodedMortgageAppId: string;
};

const buildSafeRedirectPath = (
  target: string | null,
  params: Record<string, string | null | undefined>,
): string | null => {
  if (!target) return null;
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

const UseActiveLoans = ({
  mortgageApplicationId,
  location,
  encodedBvn,
  encodedMortgageAppId,
}: UseActiveLoansProp) => {
  const [creditCheckResponses, setCreditCheckResponses] = useState<
    InitiateCreditCheckResData[] | undefined
  >();
  const [creditCheckBlocked, setCreditCheckBlocked] = useState(false);
  const [creditCheckErrorMessage, setCreditCheckErrorMessage] = useState<
    string | undefined
  >();
  const { setLoading } = useContext(LoadingContext);
  const history = useHistory();
  const { openError } = useContext(ToastContext);

  // code: 1 = record found, no active loans
  //       2 = record found, has active loans
  //       3 = no record found
  //       4 = bureau service unavailable
  const getActiveLoans = async (req: InitiateCreditCheckRequest) => {
    setLoading(true);
    try {
      const { data, error } = await initiateCreditCheck(req);
      if (data) {
        if (data.code === 1 || data.code === 2 || data.code === 3) {
          setCreditCheckBlocked(false);
          setCreditCheckErrorMessage(undefined);
          if (data.data) {
            // The API returns a single object when there's one loan record,
            // or an array when the applicant has multiple. Normalize to an
            // array so consumers always deal with one shape.
            setCreditCheckResponses(
              Array.isArray(data.data) ? data.data : [data.data],
            );
          }
        } else {
          const message =
            data.code === 4
              ? "We're unable to reach the credit bureau service right now. Please try again shortly."
              : data.message ||
                "Unable to verify your credit history at this time.";

          setCreditCheckResponses(undefined);
          setCreditCheckBlocked(true);
          setCreditCheckErrorMessage(message);
          openError(message);
        }
      }
      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred",
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const submitLoans = async (request: SubmitExistingLoansRequest) => {
    setLoading(true);
    try {
      const redirect = localStorage.getItem("mortgage_redirect_path");
      const { data, error } = await submitExistingLoans(request);
      if (data) {
        const safePath = buildSafeRedirectPath(redirect, {
          loc: location !== undefined ? String(location) : undefined,
          app: encodedBvn,
          dippa: encodedMortgageAppId,
        });
        if (safePath) {
          history.replace(safePath);
        } else {
          openError("An unknown error occurred");
        }
      }
      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred",
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // A previously fetched result belongs to whatever location it was
  // fetched for. If the user changes location, invalidate it so the
  // effect below re-fetches for the new location.
  useEffect(() => {
    setCreditCheckResponses(undefined);
    setCreditCheckBlocked(false);
    setCreditCheckErrorMessage(undefined);
  }, [location]);

  useEffect(() => {
    if (
      !creditCheckResponses &&
      mortgageApplicationId &&
      location !== undefined
    ) {
      getActiveLoans({
        mortgageApplicationId: mortgageApplicationId,
        location,
      });
    }
  }, [creditCheckResponses, mortgageApplicationId, location]);

  return {
    models: {
      creditCheckResponses,
      creditCheckBlocked,
      creditCheckErrorMessage,
    },
    operations: {
      getActiveLoans,
      submitLoans,
    },
  };
};

export default UseActiveLoans;
